import { Component, ElementRef, OnDestroy, OnInit, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef, ColGroupDef, GridApi, ModuleRegistry, GridOptions, GridReadyEvent, ProcessHeaderForExportParams, ProcessCellForExportParams } from '@ag-grid-community/core';
import { HttpClient, HttpClientModule, HttpHeaders, HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { GridChartsModule } from '@ag-grid-enterprise/charts';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { GridData } from '../grid-data.model';
import { colDefs as importedColDefs } from '../column-definitions';
import { interval, Subscription, Observable, EMPTY } from 'rxjs';
import { startWith, switchMap, tap, catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { LicenseManager } from "@ag-grid-enterprise/core";
import { RichSelectModule } from "@ag-grid-enterprise/rich-select";
import { NotificationContainerComponent } from '../notification-container/notification-container.component';
import { NotificationService } from '../notification/notification.service';
import { ApiConfigService } from '../services/api-config.service';
import { firstValueFrom } from 'rxjs';

ModuleRegistry.registerModules([
  CsvExportModule,
  ClientSideRowModelModule,
  StatusBarModule,
  RowGroupingModule,
  ServerSideRowModelModule,
  GridChartsModule,
  ClipboardModule,
  ExcelExportModule,
  RangeSelectionModule,
  SetFilterModule,
  MenuModule,
  RichSelectModule
]);

LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-067761}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Definio_Solutions}_is_granted_a_{Multiple_Applications}_Developer_License_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Charts_and_AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{24_September_2025}____[v3]_[0102]_MTc1ODY2ODQwMDAwMA==c7d7802f12faa78789f4e5b929adda31");

interface JsonObject {
  [key: string]: any;
}

type AllowedValuesFields = 'Status_S' | 'Material_Type_S' | 'Material_Sub_Type_S' | 'Base_Unit_of_Measure_S';

@Component({
  selector: 'app-harmonization-data',
  standalone: true,
  imports: [CommonModule, FormsModule, AgGridAngular, HttpClientModule, NotificationContainerComponent],
  templateUrl: './harmonization-data.component.html',
  styleUrls: ['./harmonization-data.component.scss']
})
export class HarmonizationDataComponent implements OnInit, OnDestroy {
  private gridApi!: GridApi;
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
  autoSaveEnabled: boolean = true;

  public isToolsDropdownOpen: boolean = false;
  public isSettingsDropdownOpen: boolean = false;
  public isLoading: boolean = false;
  public loadingMessage: string = 'Loading data...';

  @ViewChild('toolsDropdown') toolsDropdown!: ElementRef;
  @ViewChild('settingsDropdown') settingsDropdown!: ElementRef;
  @HostListener('document:click', ['$event.target'])
  onDocumentClick(targetElement: any) {
    const clickedInsideTools = this.toolsDropdown?.nativeElement.contains(targetElement);
    const clickedInsideSettings = this.settingsDropdown?.nativeElement.contains(targetElement);

    if (!clickedInsideTools && this.isToolsDropdownOpen) {
      this.isToolsDropdownOpen = false;
    }
    if (!clickedInsideSettings && this.isSettingsDropdownOpen) {
      this.isSettingsDropdownOpen = false;
    }
  }

  toggleDropdown(dropdown: string) {
    if (dropdown === 'tools') {
      this.isToolsDropdownOpen = !this.isToolsDropdownOpen;
      if (this.isToolsDropdownOpen) {
        this.isSettingsDropdownOpen = false;
      }
    } else if (dropdown === 'settings') {
      this.isSettingsDropdownOpen = !this.isSettingsDropdownOpen;
      if (this.isSettingsDropdownOpen) {
        this.isToolsDropdownOpen = false;
      }
    }
  }

  private autoSaveSubscription!: Subscription;

  public isPaginationVisible: boolean = false;

  private parsingDefinitions: any[] = [];
  private changedRows: { [id: string]: GridData } = {};

  forbiddenChars = ['Ø', '#', '\'', '*', '°', ';', '"', '?', '!', '\\', '|', '[', ']', '{', '}'];
  mandatoryFields = ['Status_S', 'Material_Type_S', 'Material_Sub_Type_S', 'Base_Unit_of_Measure_S', 'Material_Group_S', 'Description_EN_S', 'Description_FI_S'];
  allowedValues: { [K in AllowedValuesFields]: string[] } = {
    'Status_S': ['Standardized - Ready for validation', 'Unclear', 'Unclear - To be replaced', 'Unclear - To be deleted', 'Material Deleted', 'Not in scope - Wrong material type'],
    'Material_Type_S': ['ZRSA', 'ZROH', 'ZERP', 'ZBAT', 'ZBPS'],
    'Material_Sub_Type_S': ['General item', 'Standard item', 'Manufacturer specific item', 'Equipment Supplier specific item', 'Metsä Drawing item'],
    'Base_Unit_of_Measure_S': ['PC', 'KG', 'L', 'M', 'M2', 'M3', 'T', 'MWH', 'DT', 'ROL']
  };

  readonly SKIP_VALIDATION_FIELDS = [
    // Comments and Classification
    'Comments_S',
    'Case_Classification_S',
    
    // Customs and Commodity fields
    'Commodity_Code_S',
    '1st_Customs_Unit_S',
    '2nd_Customs_Unit_S',
    
    // Basic Name fields
    'Basic_Name_EN_S',
    'Basic_Name_FI_S',
    'Basic_Name_SV_S',
    'Basic_Name_ET_S',
    'Basic_Name_DE_S',
    'Basic_Name_PL_S',
    'Basic_Name_SK_S',
    
    // EAN fields
    'InternationalArticleNumber_EAN_UPC_S',
    'Main_EAN_S',
    'EAN_category_For_EAN_in_Base_UoM_S',
    
    // Additional Information fields
    'Add_Information1_S',
    'Add_Information2_S',
    
    // Plant and Price fields
    'Plants',
    'No_of_plants', 
    'Net_price',
    
    // Type Parsed fields
    'Type_Parsed_EN_A',
    'Type_Parsed_FI_A',
    'Type_Parsed_EN_FI_Comparison',
    'Type_S_Type_Parsed_EN_Comparison',
    
    // Description Rule field
    'Description_Forming_Rule_S',
    
    // DKB fields - using startsWith to catch all DKB fields
    'DKB_ID',
    'DKB_ManufacturerID'
  ];

  readonly STATUS_VALUES = [
    'Not started',
    'Categorized',
    'Data parsed',
    'Under work',
    'Standardized - Check with BA',
    'Standardized - To be reviewed',
    'Standardized',
    'Standardized - Ready for validation',
    'Unclear',
    'Standardized - Delivered',
    'Unclear - Delivered',
    'Material Deleted',
    'Not in scope'
  ];

  readonly TEXT_LENGTH_CONSTRAINTS = {
    DESCRIPTION_FIELDS: [
      'Description_EN_S',
      'Description_FI_S',
      'Description_SV_S',
      'Description_ET_S',
      'Description_DE_S',
      'Description_PL_S',
      'Description_SK_S',
      'Characteristic_Basic_Name_S'
    ],
    CLASSIFICATION_FIELDS: [
      'Manufacturer_S',
      'Manufacturers_Item_S',
      'Type_S',
      'Type2_Other_Product_Code_S',
      'Measure1_Value1_S',
      'Measure2_Value2_S',
      'Measure3_Value3_S',
      'Measure4_Value4_S',
      'Measure5_Value5_S',
      'Material_S',
      'Standard_S',
      'Metsä_Drawing_S',
      'Equipment_Supplier_S',
      'Equipment_Suppliers_ID_S',
      'SSG_Number_S',
      'RS_Number_S'
    ],
    SKIP_LENGTH_VALIDATION: [
      'Comments_S',
      'DKB_ID',
      'Basic_Name_EN_S',
      'Basic_Name_FI_S',
      'Basic_Name_SV_S',
      'Basic_Name_ET_S',
      'Basic_Name_DE_S',
      'Basic_Name_PL_S',
      'Basic_Name_SK_S'
    ]
  };

  readonly LENGTH_LIMITS = {
    DESCRIPTION: 40,
    CLASSIFICATION: 30
  };

  descriptionCellStyle(params: any): any {
    if (!params.value) return null;
    
    const textLength = String(params.value).length;
    const isChanged = this.changedRows[params.node.data.ID_L];
    
    if (this.TEXT_LENGTH_CONSTRAINTS.DESCRIPTION_FIELDS.includes(params.colDef.field)) {
      if (textLength > this.LENGTH_LIMITS.DESCRIPTION || (typeof params.value === 'string' && this.forbiddenChars.some(char => params.value.includes(char)))) {
        return { border: '2px solid red' };
      } else if (isChanged) {
        return { border: '' };
      }
    }
    
    return null;
  }  
  
  classificationDataCellStyle(params: any): any {
    if (!params.value) return null;
    
    const textLength = String(params.value).length;
    const isChanged = this.changedRows[params.node.data.ID_L];
    
    if (this.TEXT_LENGTH_CONSTRAINTS.CLASSIFICATION_FIELDS.includes(params.colDef.field)) {
      if (textLength > this.LENGTH_LIMITS.CLASSIFICATION || (typeof params.value === 'string' && this.forbiddenChars.some(char => params.value.includes(char)))) {
        return { border: '2px solid red' };
      } else if (isChanged) {
        return { border: '' };
      }
    }
    
    return null;
  }  

  public harmonizedFieldHeaders: string[] = [
    "ID_L", "System_L", "Material_Number_L", "Batch", "Material_Type_L", "Base_Unit_of_Measure_L",
    "Material_Group_L", "Industry_Standard_Description_PUMA_ID_L", "Indicator_In_Bulk_Liquid_L",
    "Industry_Sector_L", "Division_L", "General_item_category_group_L", "Cross_plant_matl_status_L",
    "Cross_plant_matl_status_valid_from_L", "Gross_Weight_L", "Net_Weight_L", "Weight_Unit_L",
    "Volume_L", "Volume_Unit_L", "Purchasing_Value_Key_L", "Numbering_Scheme_L",
    "Acknowledgement_Required_L", "Batch_Management_L", "Order_Unit_L", "Variable_Order_Unit_L",
    "Commodity_Code_L", "Commodity_Code_Unit_L", "1st_Customs_Unit_L", "2nd_Customs_Unit_L",
    "Description_EN_L", "Description_FI_L", "Description_SV_L", "Description_ET_L", "Description_DE_L",
    "Description_PL_L", "Description_SK_L", "Characteristic_Basic_Name_L", "Manufacturer_L",
    "Manufacturers_Item_L", "Type_L", "Type2_Other_Product_Code_L", "Measure1_Value1_L", "Measure2_Value2_L",
    "Measure3_Value3_L", "Measure4_Value4_L", "Measure5_Value5_L", "Material_L", "Standard_L",
    "Metsä_Drawing_L", "Info1_L", "Info2_L", "Common_spare_part_L", "InternationalArticleNumber_EAN_UPC_L",
    "Main_EAN_L", "EAN_category_For_EAN_in_Base_UoM_L", "PO_Text_EN_L", "PO_Text_FI_L", "PO_Text_SV_L",
    "PO_Text_ET_L", "PO_Text_DE_L", "PO_Text_PL_L", "PO_Text_SK_L", "Basic_Data_Text_EN_L",
    "Basic_Data_Text_FI_L", "Basic_Data_Text_SV_L", "Basic_Data_Text_ET_L", "Basic_Data_Text_DE_L",
    "Basic_Data_Text_PL_L", "Basic_Data_Text_SK_L", "ID_S", "System_S", "Material_Number_S", "Status_S",
    "Comments_S", "Comments_BA", "Case_Classification_S", "Material_Type_S", "Material_Sub_Type_S",
    "Base_Unit_of_Measure_S", "Material_Group_S", "Industry_Standard_Description_PUMA_ID_S",
    "Indicator_In_Bulk_Liquid_S", "Gross_Weight_S", "Net_Weight_S", "Weight_Unit_S", "Commodity_Code_S", "1st_Customs_Unit_S", "2nd_Customs_Unit_S",
    "Description_EN_S", "Description_FI_S", "Description_SV_S", "Description_ET_S", "Description_DE_S",
    "Description_PL_S", "Description_SK_S", "Characteristic_Basic_Name_S", "Manufacturer_S",
    "Manufacturers_Item_S", "Type_S", "Type2_Other_Product_Code_S", "Measure1_Value1_S", "Measure2_Value2_S",
    "Measure3_Value3_S", "Measure4_Value4_S", "Measure5_Value5_S", "Material_S", "Standard_S",
    "Metsä_Drawing_S", "Equipment_Supplier_S", "Equipment_Suppliers_ID_S", "SSG_Number_S",
    "RS_Number_S", "InternationalArticleNumber_EAN_UPC_S", "Main_EAN_S", "EAN_category_For_EAN_in_Base_UoM_S",
    "Add_Information1_S", "Add_Information2_S", "Plants", "No_of_plants", "Net_price"
  ];

  private isInitialLoad: boolean = true;
  private validationEnabled: boolean = false;

  comparisonFieldStyle(params: any): any {
    const field = params.colDef.field;
    const value = params.value;

    // Only apply to specific comparison fields
    if (field !== 'Type_Parsed_EN_FI_Comparison' && 
        field !== 'Type_S_Type_Parsed_EN_Comparison') {
      return null;
    }

    // Return style based on TRUE/FALSE value
    return {
      borderLeft: value === 'TRUE' ? '5px solid #4CAF50' : // Green for TRUE
                  value === 'FALSE' ? '5px solid #f44336' : // Red for FALSE
                  null // No border for other values
    };
  }

  public colDefs: (ColDef | ColGroupDef)[] = [];
  public defaultColDef = {
    width: 120,
    resizable: true,
    filter: 'agSetColumnFilter',
    cellStyle: (params: any) => this.cellStyleFunc(params),
    editable: true,
    onCellValueChanged: (params: any) => this.onCellValueChanged(params),
    enableCellChangeFlash: true
  };
  public rowData: GridData[] = [];
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public gridOptions: GridOptions<GridData> = {
    getRowId: (params: { data: GridData }) => params.data.ID_L,
    enableRangeSelection: true,
    suppressRowClickSelection: true,
    statusBar: {
      statusPanels: [
        {
          statusPanel: 'agSelectedRowCountComponent',
          align: 'left'
        },
        {
          statusPanel: 'agTotalRowCountComponent',
          align: 'right'
        },
        {
          statusPanel: 'agFilteredRowCountComponent',
          align: 'right'
        }
      ]
    },
    undoRedoCellEditing: true,
  };
  public selectedRowCount: number = 0;
  public loadingHarmonizationData: boolean = false;
  public loadingDataParsingDefinitions: boolean = false;
  public error: string | null = null;
  public totalRows: number = 0;
  public loadedRows: number = 0;

  public batches: string[] = [];

  public excelStyles: any = [
    {
      id: 'legacyDataHeader',
      interior: { color: '#0e2841', pattern: 'Solid' },
      font: { color: '#ffffff' },
      alignment: { horizontal: 'Center' },
      dataType: 'header',
      columns: [],
    },
    {
      id: 'harmonizedDataHeader',
      interior: { color: '#92d050', pattern: 'Solid' },
      font: { color: '#ffffff' },
      alignment: { horizontal: 'Center' },
      dataType: 'header',
      columns: [],
    },
  ];

  autoSaveIntervalMinutes: number = 10; // Default to 10 minutes

  // Find & Replace modal state
  public isFindReplaceModalOpen: boolean = false;
  public findValue: string = '';
  public replaceValue: string = '';
  public currentMatchIndex: number = -1;
  public totalMatches: number = 0;
  public selectedCells: { rowIndex: number, colId: string, value: any }[] = [];
  public matches: { rowIndex: number, colId: string, value: any, node: any }[] = [];

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private notificationService: NotificationService,
    private apiConfig: ApiConfigService
  ) { }

  ngOnInit() {
    // Retrieve saved auto-save state at initialization
    const savedAutoSaveState = localStorage.getItem('autoSaveEnabled');
    if (savedAutoSaveState !== null) {
      this.autoSaveEnabled = savedAutoSaveState === 'true';
    }
  
    this.fetchBatches().then(
      () => {
        this.setupAutoSave();
        this.fetchDataParsingDefinitions().then(
          () => this.fetchDataFromApi()
        ).then(() => {
          this.colDefs = this.getModifiedColDefs();
        });
      }
    );
  }

  generateDescriptions(): void {
    const selectedNodes = this.gridApi.getSelectedNodes();
    const updateItems: GridData[] = [];

    selectedNodes.forEach((node) => {
      const data = node.data;
      const rule = data.Description_Forming_Rule_S;

      if (rule) {
        let descriptionEN = '';
        let descriptionFI = '';

        // Parse the rule number (e.g., '1' from '1 - BasicName & Type')
        const ruleNumber = rule.split(' - ')[0];

        switch (ruleNumber) {
          case '1':
            descriptionEN = `${data.Basic_Name_EN_S || ''} ${data.Type_S || ''}`.trim();
            descriptionFI = `${data.Basic_Name_FI_S || ''} ${data.Type_S || ''}`.trim();
            break;
          case '2':
            descriptionEN = `${data.Basic_Name_EN_S || ''} ${data.Measure1_Value1_S || ''} ${data.Material_S || ''}`.trim();
            descriptionFI = `${data.Basic_Name_FI_S || ''} ${data.Measure1_Value1_S || ''} ${data.Material_S || ''}`.trim();
            break;
          case '3':
            descriptionEN = `${data.Basic_Name_EN_S || ''} ${data.Measure1_Value1_S || ''} ${data.Material_S || ''} ${data.Standard_S || ''}`.trim();
            descriptionFI = `${data.Basic_Name_FI_S || ''} ${data.Measure1_Value1_S || ''} ${data.Material_S || ''} ${data.Standard_S || ''}`.trim();
            break;
          case '4':
            descriptionEN = `${data.Basic_Name_EN_S || ''} ${data.Manufacturers_Item_S || ''} ${data.Type_S || ''}`.trim();
            descriptionFI = `${data.Basic_Name_FI_S || ''} ${data.Manufacturers_Item_S || ''} ${data.Type_S || ''}`.trim();
            break;
          case '5':
            descriptionEN = `${data.Basic_Name_EN_S || ''} ${data.Manufacturers_Item_S || ''} ${data.Measure1_Value1_S || ''}`.trim();
            descriptionFI = `${data.Basic_Name_FI_S || ''} ${data.Manufacturers_Item_S || ''} ${data.Measure1_Value1_S || ''}`.trim();
            break;
          case '6':
            descriptionEN = `${data.Basic_Name_EN_S || ''} ${data.Equipment_Suppliers_ID_S || ''} ${data.Type_S || ''}`.trim();
            descriptionFI = `${data.Basic_Name_FI_S || ''} ${data.Equipment_Suppliers_ID_S || ''} ${data.Type_S || ''}`.trim();
            break;
          case '7':
            descriptionEN = `${data.Basic_Name_EN_S || ''} ${data.Equipment_Suppliers_ID_S || ''} ${data.Measure1_Value1_S || ''}`.trim();
            descriptionFI = `${data.Basic_Name_FI_S || ''} ${data.Equipment_Suppliers_ID_S || ''} ${data.Measure1_Value1_S || ''}`.trim();
            break;
          case '8':
            descriptionEN = `${data.Basic_Name_EN_S || ''} ${data.Equipment_Suppliers_ID_S || ''} ${data.Measure1_Value1_S || ''} ${data.Measure2_Value2_S || ''}`.trim();
            descriptionFI = `${data.Basic_Name_FI_S || ''} ${data.Equipment_Suppliers_ID_S || ''} ${data.Measure1_Value1_S || ''} ${data.Measure2_Value2_S || ''}`.trim();
            break;
          default:
            // Handle invalid or unsupported rule numbers
            console.warn(`Unsupported rule number: ${ruleNumber}`);
            return;
        }

        // Update the data object
        data.Description_EN_S = descriptionEN;
        data.Description_FI_S = descriptionFI;

        // Mark the row as changed
        this.changedRows[data.ID_L] = data;

        // Add to update items for grid transaction
        updateItems.push(data);
      } else {
        console.warn(`No Description_Forming_Rule_S specified for ID: ${data.ID_L}`);
      }
    });

    if (updateItems.length > 0) {
      this.gridApi.applyTransaction({ update: updateItems });
      console.log('Descriptions generated and grid updated.');
    } else {
      console.log('No descriptions generated. Please ensure that Description_Forming_Rule_S is selected for the selected rows.');
    }
  }

  togglePaginationVisibility(): void {
    this.isPaginationVisible = !this.isPaginationVisible;
    if (this.gridApi) {
      if (this.isPaginationVisible) {
        this.gridApi.setGridOption('pagination', true);
      } else {
        this.gridApi.setGridOption('pagination', false);
      }
      // Force the grid to re-render
      this.gridApi.redrawRows();
    }
  }

  getModifiedColDefs(): (ColDef | ColGroupDef)[] {
    const newColDefs = JSON.parse(JSON.stringify(importedColDefs)) as (ColDef | ColGroupDef)[];
  
    const setFieldProperties = (colDefs: (ColDef | ColGroupDef)[]) => {
      colDefs.forEach(colDef => {
        if (isColGroupDef(colDef)) {
          setFieldProperties(colDef.children);
        } else if (isColDef(colDef)) {
          // Make legacy fields non-editable
          if (colDef.field?.endsWith('_L')) {
            colDef.editable = false;
          }
          
          // Add comparison field styling
          if (colDef.field === 'Type_Parsed_EN_FI_Comparison' || 
              colDef.field === 'Type_S_Type_Parsed_EN_Comparison') {
            colDef.cellStyle = this.comparisonFieldStyle.bind(this);
          }
          
          // Existing Status_S dropdown logic
          if (colDef.field === 'Status_S') {
            colDef.cellEditor = 'agRichSelectCellEditor';
            colDef.cellEditorParams = {
              values: this.STATUS_VALUES,
            };
            colDef.cellRendererParams = {
              getValue: (params: any) => params.value
            };
            colDef.valueFormatter = (params) => {
              return params.value || '';
            };
          }
        }
      });
    };
  
    setFieldProperties(newColDefs);
    
    // Apply existing style modifications
    const descriptionFields = [
      'Description_EN_S', 'Description_FI_S', 'Description_SV_S',
      'Description_ET_S', 'Description_DE_S', 'Description_PL_S',
      'Description_SK_S', 'Characteristic_Basic_Name_S'
    ];
    
    const classificationFields = [
      'Manufacturer_S', 'Manufacturers_Item_S', 'Type_S',
      'Type2_Other_Product_Code_S', 'Measure1_Value1_S', 'Measure2_Value2_S',
      'Measure3_Value3_S', 'Measure4_Value4_S', 'Measure5_Value5_S',
      'Material_S', 'Standard_S', 'Metsä_Drawing_S',
      'Equipment_Supplier_S', 'Equipment_Suppliers_ID_S', 'SSG_Number_S',
      'RS_Number_S'
    ];
  
    newColDefs.forEach(colDef => {
      this.applyCellStyleRecursively(colDef, descriptionFields, classificationFields);
    });
  
    return newColDefs;
  }

  applyCellStyleRecursively(
    colDef: ColDef | ColGroupDef,
    descriptionFields: string[],
    classificationFields: string[]
  ): void {
    if (isColGroupDef(colDef)) {
      // It's a ColGroupDef
      colDef.children.forEach(child => {
        this.applyCellStyleRecursively(child, descriptionFields, classificationFields);
      });
    } else if (isColDef(colDef)) {
      // It's a ColDef
      if (colDef.field && descriptionFields.includes(colDef.field)) {
        colDef.cellStyle = this.descriptionCellStyle.bind(this);
      } else if (colDef.field && classificationFields.includes(colDef.field)) {
        colDef.cellStyle = this.classificationDataCellStyle.bind(this);
      }
    }
  }

  private async fetchBatches(): Promise<string[]> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${this.authService.getToken()}`);
    try {
      const batchObjects = await firstValueFrom(this.http.get<any[]>(this.apiConfig.harmonizationBatchesUrl, { headers }));
      console.log('Batch objects received:', batchObjects);
      
      // Extract batch names from objects
      if (Array.isArray(batchObjects) && batchObjects.length > 0) {
        // Check if we're dealing with JSON objects that contain a Batch property
        if (typeof batchObjects[0] === 'object' && batchObjects[0] !== null) {
          // Extract only the Batch property from each object
          this.batches = batchObjects.map(batch => batch.Batch || '');
          console.log('Extracted batch names:', this.batches);
        } else if (typeof batchObjects[0] === 'string') {
          // API returned array of strings
          this.batches = batchObjects;
        }
      } else {
        // Fallback to static batches if API returns empty array
        const { batches: staticBatches } = await import('../../assets/batches');
        this.batches = staticBatches;
      }
      
      return this.batches;
    } catch (error) {
      console.error('Error fetching batches from API:', error);
      // Fallback to static batches
      const { batches: staticBatches } = await import('../../assets/batches');
      this.batches = staticBatches;
      return staticBatches;
    }
  }
  private async fetchDataParsingDefinitions(): Promise<any[]> {
    this.loadingDataParsingDefinitions = true;
    try {
      const headers = new HttpHeaders().set('Authorization', `Bearer ${this.authService.getToken()}`);
      const results = await firstValueFrom(this.http.get<any[]>(this.apiConfig.harmonizationDataParsingDefinitionsUrl, { headers }));
      console.log('Fetched parsing definitions:', results);
      this.parsingDefinitions = results;
      console.log('Updated parsingDefinitions:', this.parsingDefinitions);
      return results;
    } catch (error) {
      console.error('Error fetching data parsing definitions:', error);
      this.notificationService.error('Failed to load data parsing definitions');
      return [];
    } finally {
      this.loadingDataParsingDefinitions = false;
    }
  }

  private fetchDataFromApi(): Promise<void> {
    this.isLoading = true;
    this.loadingMessage = 'Fetching harmonization data...';
    this.loadingHarmonizationData = true;
    this.error = null;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${this.authService.getToken()}`);

    return new Promise((resolve, reject) => {
      this.http.get(this.apiConfig.harmonizationBaseUrl, {
        headers,
        responseType: 'text',
        reportProgress: true,
        observe: 'events'
      }).subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.DownloadProgress) {
            //this.loadedRows = event.loaded;
            //this.processFullOrPartialData(event.partialText, observer);
          } else if (event.type === HttpEventType.Response) {
            if (event.body === null) {
              console.error('Error fetching data from API', event);
              this.error = 'Error fetching data from API';
              this.loadingHarmonizationData = false;
              reject('Error fetching data from API');
              this.isLoading = false;
            } else {
              this.processFullOrPartialData(event.body, resolve);
              this.isLoading = false
            }
          }
        },
        error: (error) => {
          console.error('Error fetching data from API', error);
          this.error = 'Error fetching data from API';
          this.loadingHarmonizationData = false;
          this.isLoading = false;
          reject(error);
        }
      });
    });
  }

  processFullOrPartialData(data: string, resolve: any): void {
    const chunks = data.split('\n').filter((chunk: string) => chunk.trim() !== '');

    const totalCountObj = JSON.parse(chunks[0]);
    this.totalRows = totalCountObj.data.length;

    let allProcessedData: GridData[] = [...totalCountObj.data];

    // Initialize with empty validation status and no validation
    allProcessedData = allProcessedData.map(row => ({
      ...row,
      Validation_Status: '' // Start with empty validation status
    }));

    this.rowData = allProcessedData;

    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', allProcessedData);
    }

    this.loadingHarmonizationData = false;
    resolve();
  }

  processChunk(chunk: GridData[]): GridData[] {
    // Apply parsing definitions to each row in the chunk
    return chunk.map(row => this.applyParsingDefinitions(row));
  }

  cleanBrokenJson(input: string, parseResult: boolean = false): string | any {
    // Remove any leading/trailing square brackets and split the input
    const trimmedInput = input.replace(/^\[|\]$/g, '');
    const jsonStrings = trimmedInput.split(/,(?={)/);

    // Process each JSON string
    const cleanedJsonStrings = jsonStrings.map((jsonString) => {
      // Ensure each string is a complete JSON object
      if (!jsonString.startsWith('{')) {
        jsonString = '{' + jsonString;
      }
      if (!jsonString.endsWith('}')) {
        jsonString += '}';
      }

      try {
        // Attempt to parse the JSON to validate it
        JSON.parse(jsonString);
        return jsonString;
      } catch (error) {
        // If parsing fails, return null (will be filtered out later)
        return null;
      }
    });

    // Filter out null values and join the valid JSON strings
    const cleanedJsonString = '[' + cleanedJsonStrings.filter(Boolean).join(',') + ']';

    // Return either the string or parsed JSON based on the parseResult parameter
    return parseResult ? JSON.parse(cleanedJsonString) : cleanedJsonString;
  }
  // Find and Replace need to start with a capital letter
  applyParsingDefinitions(row: GridData): GridData {
    if (!this.parsingDefinitions || this.parsingDefinitions.length === 0) {
      console.warn('No parsing definitions available when trying to apply them');
      return row;
    }
    
    console.log('Applying parsing definitions to row:', row.ID_L);
    
    this.parsingDefinitions.forEach(def => {
      if (!def || !def.Find || !def.Replace) {
        console.warn('Invalid parsing definition encountered:', def);
        return;
      }
      
      if (row.Basic_Name_EN_S && typeof row.Basic_Name_EN_S === 'string' && row.Basic_Name_EN_S.includes(def.Find)) {
        console.log('Applying definition to EN name:', {
          field: 'Basic_Name_EN_S',
          oldValue: row.Basic_Name_EN_S,
          find: def.Find,
          replace: def.Replace
        });
        row.Basic_Name_EN_S = row.Basic_Name_EN_S.replace(def.Find, def.Replace);
      }
      
      if (row.Basic_Name_FI_S && typeof row.Basic_Name_FI_S === 'string' && row.Basic_Name_FI_S.includes(def.Find)) {
        console.log('Applying definition to FI name:', {
          field: 'Basic_Name_FI_S',
          oldValue: row.Basic_Name_FI_S,
          find: def.Find,
          replace: def.Replace
        });
        row.Basic_Name_FI_S = row.Basic_Name_FI_S.replace(def.Find, def.Replace);
      }
    });
    
    return row;
  }

  onGridReady(params: GridReadyEvent<GridData>): void {
    this.gridApi = params.api;
    this.gridApi.setGridOption('rowData', []);
    this.gridApi.addEventListener('cellValueChanged', this.trackChanges.bind(this));
  }

  trackChanges(event: any): void {
    const changedData = event.data;
    const id = changedData.ID_L;
    this.changedRows[id] = changedData;
  }

  refreshFromDatabase(): void {
    this.validationEnabled = false; // Reset validation state on refresh
    this.rowData = [];
    this.loadedRows = 0;
    this.fetchDataParsingDefinitions().then(
      () => this.fetchDataFromApi()
    ).then(() => {
      console.log('Data refreshed from the API');
    });
  }

  onBtnExport(type: string): void {
    if (type === 'csv') {
      this.gridApi.exportDataAsCsv({
        columnSeparator: ';',
        processCellCallback: (params) => {
          return typeof params.value === 'string' ? `'${params.value}` : params.value;
        }
      });
    } else if (type === 'excel') {
      const legacyFields = this.harmonizedFieldHeaders.filter(
        (field) => field.endsWith('_L') || field === 'ID_L' || field === 'Batch'
      );
      const harmonizedFields = this.harmonizedFieldHeaders.filter(
        (field) => field.endsWith('_S') || field === 'ID_S' ||
          field === 'Comments_BA' || field === 'Plants' ||
          field === 'No_of_plants' || field === 'Net_price' ||
          field === 'Gross_Weight_S' || field === 'Net_Weight_S' || 
          field === 'Weight_Unit_S'
      );

      const allFieldsToExport = [...legacyFields, ...harmonizedFields];

      this.excelStyles = [
        {
          id: 'legacyDataHeader',
          interior: { color: '#0e2841', pattern: 'Solid' },
          font: { color: '#ffffff' },
          alignment: { horizontal: 'Center' },
          dataType: 'string',
          columns: legacyFields,
        },
        {
          id: 'harmonizedDataHeader',
          interior: { color: '#92d050', pattern: 'Solid' },
          font: { color: '#ffffff' },
          alignment: { horizontal: 'Center' },
          dataType: 'string',
          columns: harmonizedFields,
        },
      ];

      this.gridApi.exportDataAsExcel({
        fileName: 'HarmonizedDataExport.xlsx',
        sheetName: 'HarmonizedData',
        columnKeys: allFieldsToExport,
        skipColumnGroupHeaders: true,
        processHeaderCallback: (params: ProcessHeaderForExportParams): string => {
          const headerName = params.column.getColDef().headerName;
          const field = params.column.getColDef().field;
          return headerName !== undefined ? headerName : field !== undefined ? field : '';
        },
        processCellCallback: (params: ProcessCellForExportParams) => {
          // Convert all values to strings and prefix with a single quote
          return typeof params.value === 'string' ? `'${params.value}` : `'${params.value}`;
        },
      });
    }
  }

  bulkCreateData(): void {
    let currentRowData: GridData[] = [];
    this.gridApi.forEachNode(node => currentRowData.push(node.data as GridData));
    const token = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    this.http.post(this.apiConfig.harmonizationBulkUrl, { dataArray: currentRowData }, { headers })
      .subscribe(
        (response) => {
          console.log('Bulk create successful', response);
          this.refreshFromDatabase();
        },
        (error) => {
          console.error('Bulk create error', error);
        }
      );
  }

  bulkUpdateData(): void {
    const removeAllLegacyFields = (data: any) => {
      const filteredData: any = {};
      Object.keys(data).forEach(key => {
        if (key === 'ID_L' || !key.endsWith('_L')) {
          filteredData[key] = data[key];
        }
      });
      return filteredData;
    };
  
    const changedData = Object.values(this.changedRows).map(row => 
      removeAllLegacyFields(row)
    );
  
    if (changedData.length === 0) {
      this.notificationService.info('No changes to save');
      return;
    }
  
    console.log('Sending changed data:', JSON.stringify(changedData, null, 2));
    const token = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    
    this.http.put(this.apiConfig.harmonizationBulkUrl, 
      { dataArray: changedData }, 
      { headers }
    ).subscribe({
      next: (response) => {
        console.log('Bulk update successful', response);
        this.notificationService.success('Changes saved successfully');
        this.refreshFromDatabase();
        this.changedRows = {};
      },
      error: (error) => {
        console.error('Bulk update error', error);
        this.notificationService.error('Failed to save changes. Please try again or contact support if the issue persists.');
      }
    });
  }

  bulkDeleteData(): void {
    const selectedNodes = this.gridApi.getSelectedNodes();
    const idsToDelete = selectedNodes.map(node => node.data.ID);
    const token = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    this.http.request('delete', this.apiConfig.harmonizationBulkUrl, { body: idsToDelete, headers })
      .subscribe(
        (response) => {
          console.log('Bulk delete successful', response);
          this.refreshFromDatabase();
        },
        (error) => {
          console.error('Bulk delete error', error);
        }
      );
  }

  onFilterTextBoxChanged(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.agGrid.api.setQuickFilter(inputValue);
  }

  filterByBatch(event: Event): void {
    const selectedBatch = (event.target as HTMLSelectElement).value;
    
    // Update the filter model of the grid
    if (selectedBatch) {
      // Apply filter
      const filterInstance = this.gridApi.getFilterInstance('Batch');
      if (filterInstance) {
        filterInstance.setModel({
          values: [selectedBatch]
        });
        this.gridApi.onFilterChanged();
      }
    } else {
      // Clear filter
      this.gridApi.setFilterModel(null);
    }
  }

  clearAllFilters(): void {
    this.gridApi.setFilterModel(null);
  }

  copySelectedRowsData(): void {
    const selectedNodes = this.gridApi.getSelectedNodes();
    const selectedData = selectedNodes.map(node => node.data as GridData);
  
    if (selectedData.length === 0) {
      this.notificationService.info('No rows selected. Please select at least one row to copy data from Legacy Data.');
      return;
    }
  
    if (confirm('Are you sure you want to copy values from Legacy Data? This will overwrite existing values in Harmonized Data fields for the selected rows.')) {
      const updates = selectedData.map(rowData => ({
        ...rowData,
        Base_Unit_of_Measure_S: rowData.Base_Unit_of_Measure_L,
        Material_Group_S: rowData.Material_Group_L,
        Commodity_Code_S: rowData.Commodity_Code_L,
        Basic_Name_EN_S: rowData.Description_EN_L,
        Basic_Name_FI_S: rowData.Description_FI_L,
        Basic_Name_SV_S: rowData.Description_SV_L,
        Basic_Name_ET_S: rowData.Description_ET_L,
        Basic_Name_DE_S: rowData.Description_DE_L,
        Basic_Name_PL_S: rowData.Description_PL_L,
        Basic_Name_SK_S: rowData.Description_SK_L,
        Characteristic_Basic_Name_S: rowData.Characteristic_Basic_Name_L,
        Manufacturer_S: rowData.Manufacturer_L,
        Manufacturers_Item_S: rowData.Manufacturers_Item_L,
        Type_S: rowData.Type_L,
        Type2_Other_Product_Code_S: rowData.Type2_Other_Product_Code_L,
        Measure1_Value1_S: rowData.Measure1_Value1_L,
        Measure2_Value2_S: rowData.Measure2_Value2_L,
        Measure3_Value3_S: rowData.Measure3_Value3_L,
        Measure4_Value4_S: rowData.Measure4_Value4_L,
        Measure5_Value5_S: rowData.Measure5_Value5_L,
        Material_S: rowData.Material_L,
        Standard_S: rowData.Standard_L,
        Metsä_Drawing_S: rowData.Metsä_Drawing_L,
        Add_Information1_S: rowData.Info1_L,
        Add_Information2_S: rowData.Info2_L,
        // Added weight fields
        Gross_Weight_S: rowData.Gross_Weight_L,
        Net_Weight_S: rowData.Net_Weight_L,
        Weight_Unit_S: rowData.Weight_Unit_L
      }));
  
      this.gridApi.applyTransaction({ update: updates });
  
      // Mark changed rows for saving
      updates.forEach(update => {
        this.changedRows[update.ID_L] = update;
      });
  
      console.log('Data copied, grid updated, and changes marked for saving.');
    }
  }

  addDelimiterToDescriptions(): void {
    console.log('addDelimiterToDescriptions called at:', new Date().toISOString());
    console.log('Current state of parsingDefinitions:', JSON.stringify(this.parsingDefinitions));
    
    const selectedNodes = this.gridApi.getSelectedNodes();
    
    if (selectedNodes.length === 0) {
      this.notificationService.info('No rows selected. Please select at least one row to prepare data for parsing.');
      return;
    }
  
    const updateItems: GridData[] = [];
    
    // Debug logging for parsing definitions
    console.log('Parsing Definitions before processing:', this.parsingDefinitions);
  
    selectedNodes.forEach((rowNode) => {
      // Skip if rowNode or data is null/undefined
      if (!rowNode?.data) return;
  
      let descriptionEN = rowNode.data.Basic_Name_EN_S;
      let descriptionFI = rowNode.data.Basic_Name_FI_S;
      let modified = false;
  
      // Debug logging for current row
      console.log('Processing row:', {
        id: rowNode.data.ID_L,
        descriptionEN,
        descriptionFI
      });
  
      // Only process if descriptions exist
      if (descriptionEN || descriptionFI) {
        this.parsingDefinitions.forEach(def => {
          if (!def.Find || !def.Replace) {
            console.log('Invalid parsing definition:', def);
            return;
          }
  
          // Process English description if it exists
          if (descriptionEN) {
            const newDescriptionEN = descriptionEN.replace(def.Find, def.Replace);
            if (newDescriptionEN !== descriptionEN) {
              console.log('EN Match found:', {
                original: descriptionEN,
                new: newDescriptionEN,
                pattern: def.Find
              });
              descriptionEN = newDescriptionEN;
              modified = true;
            }
          }
  
          // Process Finnish description if it exists
          if (descriptionFI) {
            const newDescriptionFI = descriptionFI.replace(def.Find, def.Replace);
            if (newDescriptionFI !== descriptionFI) {
              console.log('FI Match found:', {
                original: descriptionFI,
                new: newDescriptionFI,
                pattern: def.Find
              });
              descriptionFI = newDescriptionFI;
              modified = true;
            }
          }
        });
  
        if (modified) {
          const updatedData = {
            ...rowNode.data,
            Basic_Name_EN_S: descriptionEN,
            Basic_Name_FI_S: descriptionFI
          };
          updateItems.push(updatedData);
  
          // Mark changed row for saving
          this.changedRows[updatedData.ID_L] = updatedData;
          
          console.log('Updated row:', {
            id: updatedData.ID_L,
            newDescriptionEN: descriptionEN,
            newDescriptionFI: descriptionFI
          });
        }
      }
    });
  
    if (updateItems.length > 0) {
      this.gridApi.applyTransaction({ update: updateItems });
      console.log(`Updated ${updateItems.length} descriptions with delimiters.`);
      this.notificationService.success(`Successfully prepared ${updateItems.length} rows for parsing.`);
    } else {
      console.log('No descriptions were updated. This might mean:');
      console.log('1. No matching patterns were found');
      console.log('2. The descriptions are empty');
      console.log('3. The parsing definitions are not loaded correctly');
      this.notificationService.info('No matching patterns found in the selected rows.');
    }
  
    // Refresh the grid to ensure updates are visible
    this.gridApi.refreshCells({
      force: true,
      columns: ['Basic_Name_EN_S', 'Basic_Name_FI_S']
    });
  }

  generateDKBID(): void {
    const selectedNodes = this.gridApi.getSelectedNodes();

    selectedNodes.forEach((node) => {
      const data = node.data;
      if (data.Manufacturer_S && data.Manufacturers_Item_S) {
        data.DKB_ID = `${data.Manufacturer_S}_${data.Manufacturers_Item_S}`;
      } else {
        data.DKB_ID = '';
      }
      node.setData(data);

      // Mark changed row for saving
      this.changedRows[data.ID_L] = data;
    });

    this.gridApi.refreshCells({ force: true });
    console.log('DKB_ID generated and changes marked for saving.');
  }

  parseDescriptions(): void {
    const selectedNodes = this.gridApi.getSelectedNodes();
    
    if (selectedNodes.length === 0) {
      this.notificationService.info('No rows selected. Please select at least one row to parse descriptions.');
      return;
    }
  
    const updateItems: GridData[] = [];
    let parsedCount = 0;
  
    selectedNodes.forEach((rowNode) => {
      let modified = false;
  
      if (rowNode.data.Basic_Name_EN_S && typeof rowNode.data.Basic_Name_EN_S === 'string') {
        const partsEN = rowNode.data.Basic_Name_EN_S.split("|");
        if (partsEN.length > 1) {
          modified = true;
          rowNode.data.Basic_Name_EN_S = partsEN[0];
          rowNode.data.Type_Parsed_EN_A = partsEN[1];
        }
      }
  
      if (rowNode.data.Basic_Name_FI_S && typeof rowNode.data.Basic_Name_FI_S === 'string') {
        const partsFI = rowNode.data.Basic_Name_FI_S.split("|");
        if (partsFI.length > 1) {
          modified = true;
          rowNode.data.Basic_Name_FI_S = partsFI[0];
          rowNode.data.Type_Parsed_FI_A = partsFI[1];
        }
      }
  
      if (modified) {
        updateItems.push({
          ...rowNode.data
        });
  
        // Mark changed row for saving
        this.changedRows[rowNode.data.ID_L] = rowNode.data;
        parsedCount++;
      }
    });
  
    if (updateItems.length > 0) {
      this.gridApi.applyTransaction({ update: updateItems });
      this.notificationService.success(`Successfully parsed ${parsedCount} rows.`);
      console.log('Descriptions parsed and changes marked for saving.');
    } else {
      this.notificationService.info('No descriptions found to parse in the selected rows. Make sure to prepare the data first.');
    }
  
    // Refresh the grid to ensure updates are visible
    this.gridApi.refreshCells({
      force: true,
      columns: ['Basic_Name_EN_S', 'Basic_Name_FI_S', 'Type_Parsed_EN_A', 'Type_Parsed_FI_A']
    });
  }

  compareTypeParsedENFI(): void {
    const selectedNodes = this.gridApi.getSelectedNodes();
    const updateItems: GridData[] = [];

    selectedNodes.forEach((node) => {
      const data = node.data;
      const typeParsedEN = data.Type_Parsed_EN_A || '';
      const typeParsedFI = data.Type_Parsed_FI_A || '';

      // Compare and set the result
      data.Type_Parsed_EN_FI_Comparison = typeParsedEN === typeParsedFI ? 'TRUE' : 'FALSE';

      updateItems.push(data);
      // Mark changed row for saving
      this.changedRows[data.ID_L] = data;
    });

    if (updateItems.length > 0) {
      this.gridApi.applyTransaction({ update: updateItems });
      console.log('Type Parsed EN/FI comparison completed and changes marked for saving.');
    }
  }

  compareTypeSandParsedEN(): void {
    const selectedNodes = this.gridApi.getSelectedNodes();
    const updateItems: GridData[] = [];

    selectedNodes.forEach((node) => {
      const data = node.data;
      const typeS = data.Type_S || '';
      const typeParsedEN = data.Type_Parsed_EN_A || '';

      // Compare and set the result
      data.Type_S_Type_Parsed_EN_Comparison = typeS === typeParsedEN ? 'TRUE' : 'FALSE';

      updateItems.push(data);
      // Mark changed row for saving
      this.changedRows[data.ID_L] = data;
    });

    if (updateItems.length > 0) {
      this.gridApi.applyTransaction({ update: updateItems });
      console.log('Type_S and Parsed Type EN comparison completed and changes marked for saving.');
    }
  }

  ngOnDestroy() {
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
    }
  }

  toggleAutoSave(): void {
    this.autoSaveEnabled = !this.autoSaveEnabled;
    localStorage.setItem('autoSaveEnabled', this.autoSaveEnabled.toString());
    this.setupAutoSave();
    console.log('Auto-save toggled:', this.autoSaveEnabled);
  }

  setupAutoSave(): void {
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
    }
    
    if (this.autoSaveEnabled && this.autoSaveIntervalMinutes >= 1) {
      this.autoSaveSubscription = interval(this.autoSaveIntervalMinutes * 60 * 1000)
        .pipe(
          startWith(0),
          switchMap(() => this.performAutoSave())
        )
        .subscribe();
      console.log('Auto-save enabled with interval:', this.autoSaveIntervalMinutes, 'minutes');
    } else {
      console.log('Auto-save disabled');
    }
  }

  performAutoSave(): Promise<void> {
    return new Promise((resolve) => {
      console.log('Auto-save triggered...');
      const changedRowsCount = Object.keys(this.changedRows).length;

      if (changedRowsCount > 0) {
        const confirmMessage = `There are ${changedRowsCount} changed row(s). Do you want to save these changes?`;

        if (confirm(confirmMessage)) {
          console.log('Auto-saving data...');
          this.bulkUpdateData();
        } else {
          console.log('Auto-save cancelled by user.');
        }
      } else {
        console.log('No changes to save, skipping auto-save.');
      }

      resolve();
    });
  }


  cellStyleFunc(params: any): any {
    const field = params.colDef.field;
    const value = params.value;
    const node = params.node;
  
    // For Validation_Status column
    if (field === 'Validation_Status') {
      if (!this.validationEnabled || !node.isSelected()) {
        return null;
      }
      
      const validationStatus = value || '';
      const hasErrors = validationStatus !== 'No Errors';
      return {
        borderLeft: hasErrors ? '5px solid #f44336' : '5px solid #4CAF50'
      };
    }
  
    // Skip validation if the field hasn't been changed
    if (!this.changedRows[params.node?.data?.ID_L]) {
      return null;
    }
  
    // Special handling for Material_Group_S
    if (field === 'Material_Group_S' && value) {
      if (value.length !== 6 || !/^\d+$/.test(value) || 
          this.forbiddenChars.some(char => value.includes(char))) {
        return { border: '2px solid red' };
      }
      return { border: '' };
    }
  
    // Skip validation for empty values
    if (!value || value === '') {
      return { border: '' };
    }
  
    // Check for forbidden characters for all relevant fields
    if (typeof value === 'string' && 
        !this.TEXT_LENGTH_CONSTRAINTS.SKIP_LENGTH_VALIDATION.includes(field) &&
        (this.TEXT_LENGTH_CONSTRAINTS.DESCRIPTION_FIELDS.includes(field) ||
         this.TEXT_LENGTH_CONSTRAINTS.CLASSIFICATION_FIELDS.includes(field)) &&
        this.forbiddenChars.some(char => value.includes(char))) {
      return { border: '2px solid red' };
    }
  
    // Check text length for description fields (excluding skipped fields)
    if (!this.TEXT_LENGTH_CONSTRAINTS.SKIP_LENGTH_VALIDATION.includes(field) &&
        this.TEXT_LENGTH_CONSTRAINTS.DESCRIPTION_FIELDS.includes(field)) {
      if (typeof value === 'string' && value.length > this.LENGTH_LIMITS.DESCRIPTION) {
        return { border: '2px solid red' };
      }
      return { border: '' };
    }
  
    // Check text length for classification fields (excluding skipped fields)
    if (!this.TEXT_LENGTH_CONSTRAINTS.SKIP_LENGTH_VALIDATION.includes(field) &&
        this.TEXT_LENGTH_CONSTRAINTS.CLASSIFICATION_FIELDS.includes(field)) {
      if (typeof value === 'string' && value.length > this.LENGTH_LIMITS.CLASSIFICATION) {
        return { border: '2px solid red' };
      }
      return { border: '' };
    }
  
    return { border: '' };
  }

  private getBaseValidationStyle(params: any): any {
    const style: any = {};
    
    if (params.colDef.field === 'Validation_Status') {
      if (params.value) {
        style.borderLeft = params.value === '' 
          ? '5px solid green' 
          : '5px solid red';
      }
      return style;
    }
  
    const validationStatus = params.node.data.Validation_Status || '';
    const validationMessages = validationStatus.split(';').map((msg: string) => msg.trim());
    const hasErrorForThisField = validationMessages.some((error: string) => 
      error.startsWith(`${params.colDef.field} `));
  
    if (hasErrorForThisField) {
      style.border = '2px solid red';
    } else if (this.changedRows[params.node.data.ID_L]) {
      style.border = '';
    }
    
    return style;
  }

  hasValidationError(params: any): boolean {
    const field = params.colDef.field;
    const node = params.node;
    if (!node || !node.data || !node.data.Validation_Status) return false;

    const value = params.value;
  
    if (!value) return false;
  
    // Check for forbidden characters
    if (typeof value === 'string' && 
        this.forbiddenChars.some(char => value.includes(char))) {
      return true;
    }
  
    // Check field-specific length constraints
    if (this.TEXT_LENGTH_CONSTRAINTS.DESCRIPTION_FIELDS.includes(field)) {
      if (typeof value === 'string' && value.length > this.LENGTH_LIMITS.DESCRIPTION) {
        return true;
      }
    }
  
    if (this.TEXT_LENGTH_CONSTRAINTS.CLASSIFICATION_FIELDS.includes(field)) {
      if (typeof value === 'string' && value.length > this.LENGTH_LIMITS.CLASSIFICATION) {
        return true;
      }
    }
  
    return false;
  }

  hasRealTimeError(params: any): boolean {
    const field = params.colDef.field as string;
    const value = params.value;

    // Skip validation if the field hasn't been changed
    if (!this.changedRows[params.node?.data?.ID_L]) {
      return false;
    }

    // Skip validation for empty or unchanged values
    if (value === null || value === undefined || value === '') {
      return false;
    }

    // Rest of your validation logic...
    if (typeof value === 'string' &&
      this.forbiddenChars.some(char => value.includes(char))) {
      return true;
    }

    if (typeof value === 'string' &&
      !field.startsWith('Description_') &&
      value.length > 40) {
      return true;
    }

    if (this.isAllowedValuesField(field) &&
      !this.allowedValues[field].includes(value)) {
      return true;
    }

    if (field === 'Material_Group_S' &&
      (value.length !== 6 || !/^\d+$/.test(value))) {
      return true;
    }

    return false;
  }


  hasError(params: any): boolean {
    const field = params.colDef.field as string;
    const value = params.value;

    if (value === null || value === undefined) return false;

    // Check for forbidden characters
    if (typeof value === 'string' && this.forbiddenChars.some(char => value.includes(char))) {
      return true;
    }

    // Check text length (assuming max length is 40, adjust as needed)
    if (typeof value === 'string' && value.length > 40) {
      return true;
    }

    // Check for allowed values
    if (this.isAllowedValuesField(field) && !this.allowedValues[field].includes(value)) {
      return true;
    }

    // Special check for Material_Group_S
    if (field === 'Material_Group_S' && (value.length !== 6 || !/^\d+$/.test(value))) {
      return true;
    }

    return false;
  }

  onCellValueChanged(params: any) {
    const field = params.colDef.field;
    const newValue = params.newValue;
    const node = params.node;
  
    // Skip validation for system/meta fields
    if (['Validation_Status', 'Batch'].includes(field)) {
      console.log('Skipping validation for system field:', field);
      return;
    }
  
    console.log('\nValidating field:', field);
    console.log('New value:', newValue);
  
    // Store the row in changedRows for tracking modifications
    this.changedRows[node.data.ID_L] = node.data;
  
    // Force refresh the cell to update styling
    this.gridApi.refreshCells({
      rowNodes: [node],
      columns: [field],
      force: true
    });
  }

  validateField(field: string, value: any): string | null {
    // Skip validation for empty optional fields
    if (!value && !this.mandatoryFields.includes(field)) {
      return null;
    }

    // Validate mandatory fields
    if (this.mandatoryFields.includes(field) && !value) {
      return `${field} is mandatory`;
    }

    // Skip validation for excluded fields
    if (this.SKIP_VALIDATION_FIELDS.includes(field) || field.startsWith('DKB_')) {
      return null;
    }

    // Special validation for Material_Group_S
    if (field === 'Material_Group_S') {
      if (!value) {
        return `${field} must be exactly 6 digits`;
      }
      if (value.length !== 6) {
        return `${field} must be exactly 6 digits (current length: ${value.length})`;
      }
      if (!/^\d+$/.test(value)) {
        return `${field} must contain only digits`;
      }
      return null;
    }

    // Validate string fields
    if (typeof value === 'string') {
      // Check for forbidden characters in relevant fields
      if (!this.TEXT_LENGTH_CONSTRAINTS.SKIP_LENGTH_VALIDATION.includes(field) &&
          (this.TEXT_LENGTH_CONSTRAINTS.DESCRIPTION_FIELDS.includes(field) ||
           this.TEXT_LENGTH_CONSTRAINTS.CLASSIFICATION_FIELDS.includes(field)) &&
          this.forbiddenChars.some(char => value.includes(char))) {
        return `${field} contains forbidden character: ${this.forbiddenChars.find(char => value.includes(char))}`;
      }

      // Check length for classification fields
      if (!this.TEXT_LENGTH_CONSTRAINTS.SKIP_LENGTH_VALIDATION.includes(field) &&
          this.TEXT_LENGTH_CONSTRAINTS.CLASSIFICATION_FIELDS.includes(field) &&
          this.getActualLength(value) > this.LENGTH_LIMITS.CLASSIFICATION) {
        return `${field} exceeds maximum length of ${this.LENGTH_LIMITS.CLASSIFICATION} characters (current: ${this.getActualLength(value)})`;
      }

      // Check length for description fields
      if (!this.TEXT_LENGTH_CONSTRAINTS.SKIP_LENGTH_VALIDATION.includes(field) &&
          this.TEXT_LENGTH_CONSTRAINTS.DESCRIPTION_FIELDS.includes(field) &&
          this.getActualLength(value) > this.LENGTH_LIMITS.DESCRIPTION) {
        return `${field} exceeds maximum length of ${this.LENGTH_LIMITS.DESCRIPTION} characters (current: ${this.getActualLength(value)})`;
      }
    }

    // Validate fields with allowed values
    if (this.isAllowedValuesField(field) && !this.allowedValues[field].includes(value)) {
      const allowedValues = this.allowedValues[field].join(', ');
      return `${field} must be one of: ${allowedValues}`;
    }

    return null;
  }

  private getActualLength(str: string): number {
    return str.replace(/[^\x00-\xff]/g, "**").length;
  }

  isAllowedValuesField(field: string): field is AllowedValuesFields {
    return field in this.allowedValues;
  }

  validateSelectedData() {
    if (!this.gridApi) return;

    const selectedNodes = this.gridApi.getSelectedNodes();
    
    if (selectedNodes.length === 0) {
      this.notificationService.error('No rows selected. Please select rows to validate.');
      return;
    }

    const updateItems: GridData[] = [];

    selectedNodes.forEach((node) => {
      const data = node.data;
      let rowHasChanges = false;
      const validationErrors: string[] = [];

      // Validate each harmonized field
      this.harmonizedFieldHeaders.forEach(field => {
        if (this.SKIP_VALIDATION_FIELDS.includes(field) || field.startsWith('DKB_')) {
          return;
        }

        const value = data[field];
        const error = this.validateField(field, value);
        
        if (error) {
          const validationKey = `validation_${field}`;
          data[validationKey] = error;
          validationErrors.push(`${field}: ${error}`);
          rowHasChanges = true;
        } else {
          const validationKey = `validation_${field}`;
          if (data[validationKey]) {
            delete data[validationKey];
            rowHasChanges = true;
          }
        }
      });

      // Update Validation_Status field based on validation results
      if (validationErrors.length > 0) {
        data.Validation_Status = validationErrors.join('; ');
        rowHasChanges = true;
      } else {
        data.Validation_Status = 'No Errors';
        rowHasChanges = true;
      }

      if (rowHasChanges) {
        this.changedRows[data.ID_L] = data;
        updateItems.push(data);
      }
    });

    if (updateItems.length > 0) {
      this.gridApi.applyTransaction({ update: updateItems });
      this.notificationService.success(`Validation completed for ${selectedNodes.length} rows.`);
    } else {
      this.notificationService.info('All selected rows are already valid.');
    }
  }

  openFindReplaceModal(): void {
    if (!this.gridApi) return;
    
    // Get currently selected cells/ranges
    this.updateSelectedCells();
    
    if (this.selectedCells.length === 0) {
      this.notificationService.error('No cells selected. Please select cells or ranges to search within.');
      return;
    }

    this.isFindReplaceModalOpen = true;
    this.findValue = '';
    this.replaceValue = '';
    this.currentMatchIndex = -1;
    this.totalMatches = 0;
    this.matches = [];
    this.isToolsDropdownOpen = false; // Close the tools dropdown
  }

  closeFindReplaceModal(): void {
    this.isFindReplaceModalOpen = false;
    this.findValue = '';
    this.replaceValue = '';
    this.currentMatchIndex = -1;
    this.totalMatches = 0;
    this.matches = [];
    this.selectedCells = [];
  }

  updateSelectedCells(): void {
    if (!this.gridApi) return;

    this.selectedCells = [];
    const rangeSelections = this.gridApi.getCellRanges();
    
    if (rangeSelections && rangeSelections.length > 0) {
      // Handle range selections
      rangeSelections.forEach(range => {
        const startRow = Math.min(range.startRow!.rowIndex, range.endRow!.rowIndex);
        const endRow = Math.max(range.startRow!.rowIndex, range.endRow!.rowIndex);
        
        range.columns.forEach(column => {
          for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
            const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex);
            if (rowNode && rowNode.data) {
              const colId = column.getColId();
              const value = rowNode.data[colId];
              this.selectedCells.push({
                rowIndex: rowIndex,
                colId: colId,
                value: value
              });
            }
          }
        });
      });
    } else {
      // Fallback to selected rows if no cell ranges
      const selectedNodes = this.gridApi.getSelectedNodes();
      selectedNodes.forEach(node => {
        const rowIndex = node.rowIndex!;
        this.colDefs.forEach(colDef => {
          if (isColDef(colDef) && colDef.field) {
            const value = node.data[colDef.field];
            this.selectedCells.push({
              rowIndex: rowIndex,
              colId: colDef.field,
              value: value
            });
          }
        });
      });
    }
  }

  findMatches(): void {
    if (!this.findValue.trim()) {
      this.matches = [];
      this.totalMatches = 0;
      this.currentMatchIndex = -1;
      return;
    }

    this.matches = [];
    const searchValue = this.findValue.toLowerCase();

    this.selectedCells.forEach(cell => {
      if (cell.value != null) {
        const cellValue = cell.value.toString().toLowerCase();
        if (cellValue.includes(searchValue)) {
          const rowNode = this.gridApi.getDisplayedRowAtIndex(cell.rowIndex);
          this.matches.push({
            rowIndex: cell.rowIndex,
            colId: cell.colId,
            value: cell.value,
            node: rowNode
          });
        }
      }
    });

    this.totalMatches = this.matches.length;
    this.currentMatchIndex = this.totalMatches > 0 ? 0 : -1;
  }

  findNext(): void {
    if (this.matches.length === 0) {
      this.findMatches();
    }

    if (this.matches.length === 0) {
      this.notificationService.info('No matches found.');
      return;
    }

    this.currentMatchIndex = (this.currentMatchIndex + 1) % this.matches.length;
    this.focusOnMatch(this.currentMatchIndex);
  }

  focusOnMatch(matchIndex: number): void {
    if (matchIndex < 0 || matchIndex >= this.matches.length) return;

    const match = this.matches[matchIndex];
    
    // Focus on the cell
    this.gridApi.setFocusedCell(match.rowIndex, match.colId);
    
    // Scroll to the cell
    this.gridApi.ensureIndexVisible(match.rowIndex);
    
    // Select the cell range
    this.gridApi.clearRangeSelection();
    this.gridApi.addCellRange({
      rowStartIndex: match.rowIndex,
      rowEndIndex: match.rowIndex,
      columnStart: match.colId,
      columnEnd: match.colId
    });
  }

  replaceCurrent(): void {
    if (this.currentMatchIndex < 0 || this.currentMatchIndex >= this.matches.length) {
      this.notificationService.error('No current match to replace.');
      return;
    }

    const match = this.matches[this.currentMatchIndex];
    const currentValue = match.value.toString();
    const newValue = currentValue.replace(new RegExp(this.findValue, 'gi'), this.replaceValue);
    
    // Update the data
    match.node.setDataValue(match.colId, newValue);
    
    // Mark row as changed
    this.changedRows[match.node.data.ID_L] = match.node.data;
    
    // Remove this match from the matches array
    this.matches.splice(this.currentMatchIndex, 1);
    this.totalMatches = this.matches.length;
    
    // Adjust current match index
    if (this.currentMatchIndex >= this.matches.length) {
      this.currentMatchIndex = this.matches.length - 1;
    }
    
    // Update selected cells to reflect the change
    this.updateSelectedCells();
    
    this.notificationService.success('Replaced successfully.');
  }

  replaceAll(): void {
    if (!this.findValue.trim()) {
      this.notificationService.error('Please enter a value to find.');
      return;
    }

    this.findMatches();
    
    if (this.matches.length === 0) {
      this.notificationService.info('No matches found to replace.');
      return;
    }

    let replacedCount = 0;
    const updateItems: GridData[] = [];

    this.matches.forEach(match => {
      const currentValue = match.value.toString();
      const newValue = currentValue.replace(new RegExp(this.findValue, 'gi'), this.replaceValue);
      
      // Update the data
      match.node.setDataValue(match.colId, newValue);
      
      // Mark row as changed
      this.changedRows[match.node.data.ID_L] = match.node.data;
      
      if (!updateItems.find(item => item.ID_L === match.node.data.ID_L)) {
        updateItems.push(match.node.data);
      }
      
      replacedCount++;
    });

    // Clear matches since they're all replaced
    this.matches = [];
    this.totalMatches = 0;
    this.currentMatchIndex = -1;
    
    // Update selected cells to reflect changes
    this.updateSelectedCells();
    
    this.notificationService.success(`Replaced ${replacedCount} occurrences.`);
  }
}

function isColGroupDef<TData>(colDef: ColDef<TData> | ColGroupDef<TData>): colDef is ColGroupDef<TData> {
  return (colDef as ColGroupDef<TData>).children !== undefined;
}

function isColDef<TData>(colDef: ColDef<TData> | ColGroupDef<TData>): colDef is ColDef<TData> {
  return (colDef as ColDef<TData>).field !== undefined;
}